import { Executer } from './executer';
import { API_BASE_URL, TIO_API_BASE_URL } from '../utils/constants';

/**
 * Restore token from localStorage if available
 * This is needed for HMR (Hot Module Replacement) where the module
 * is recreated but React state is preserved
 */
function getStoredToken(): string | null {
  try {
    const storedAuth = localStorage.getItem('authState');
    if (storedAuth) {
      const parsed = JSON.parse(storedAuth);
      return parsed.token || null;
    }
  } catch {
    // Ignore parsing errors
  }
  return null;
}

/**
 * API Service for backend communication
 * Uses user authentication tokens (no API key)
 */
class APIService {
  public client: Executer;
  private currentToken: string | null = null;

  constructor() {
    // Initialize with token from localStorage (for HMR support)
    const storedToken = getStoredToken();
    
    this.client = new Executer({
      baseURL: API_BASE_URL,
    }, storedToken || undefined);
    
    this.currentToken = storedToken;
    
    if (storedToken) {
      console.log('[APIService] Restored token from localStorage');
    }
  }

  /**
   * Set the user authentication token
   * Call this after successful login
   */
  setToken(token: string | null) {
    this.currentToken = token;
    if (token) {
      this.client.setAccessToken(token);
    } else {
      // Remove authorization header
      delete this.client.client.defaults.headers.common['Authorization'];
    }
  }

  /**
   * Get the current token
   */
  getToken(): string | null {
    return this.currentToken;
  }
}

/**
 * Tomorrow.io API Service
 * Uses user authentication tokens for Tomorrow.io API calls
 */
class TIOAPIService {
  public client: Executer;
  private currentToken: string | null = null;

  constructor() {
    // Initialize with token from localStorage (for HMR support)
    const storedToken = getStoredToken();
    
    this.client = new Executer({
      baseURL: TIO_API_BASE_URL,
    }, storedToken || undefined);
    
    this.currentToken = storedToken;
  }

  /**
   * Set the user authentication token
   * Call this after successful login
   */
  setToken(token: string | null) {
    this.currentToken = token;
    if (token) {
      this.client.setAccessToken(token);
    } else {
      // Remove authorization header
      delete this.client.client.defaults.headers.common['Authorization'];
    }
  }

  /**
   * Get the current token
   */
  getToken(): string | null {
    return this.currentToken;
  }
}

const apiServiceInstance = new APIService();
const tioApiServiceInstance = new TIOAPIService();

export default apiServiceInstance;
export { tioApiServiceInstance };

